import { Button } from '@optools/components/ui/button';
import { Label } from '@optools/components/ui/label';
import { Switch } from '@optools/components/ui/switch';
import { cn } from '@optools/components/ui/utils';
import { formatSecondsHHMMSS } from '@optools/utils/format';
import { Captions, CaptionsOff, Pause, Play, Volume1, Volume2, VolumeOff } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { getTrackBackground, Range } from 'react-range';

import { MediaElementContext } from './MediaElementProvider/media-element-provider';
import { MediaElementState } from './MediaElementProvider/types';
import { AudioSelector } from './audio-selector';

interface MediaSliderProps {
  disabled: boolean;
  label: string;
  max: number;
  min: number;
  onChange: (value: number | number[]) => void;
  step: number;
  toolTipLabel: string;
  values: number[];
}

const MediaSlider = ({
  disabled,
  label,
  max,
  min,
  onChange,
  step,
  toolTipLabel,
  values,
}: MediaSliderProps) => (
  <Range
    disabled={disabled}
    label={label}
    max={max}
    min={min}
    renderThumb={({ isDragged, props }) => (
      <div
        {...props}
        key={props.key}
        aria-disabled={disabled}
        className={cn(
          'group block h-3.5 w-3.5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
          disabled && 'border-secondary',
        )}>
        <div
          className={cn(
            'invisible absolute bottom-4 left-1/2 z-50 -translate-x-1/2 transform overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md group-hover:visible',
            {
              visible: isDragged,
            },
          )}>
          {toolTipLabel}
        </div>
      </div>
    )}
    renderTrack={({ children, props }) => {
      return (
        <div
          {...props}
          ref={props.ref}
          className={cn('relative h-2 w-full rounded-full')}
          style={{
            ...props.style,
            background: !disabled
              ? getTrackBackground({
                  colors: ['var(--primary)', 'var(--secondary)'],
                  max,
                  min,
                  values,
                })
              : 'var(--secondary)',
          }}>
          {children}
        </div>
      );
    }}
    step={step}
    values={values}
    onChange={onChange}
  />
);

const getVolumeIcon = (mediaState?: MediaElementState) => {
  const { isMuted, volume } = mediaState ?? { isMuted: true, volume: 1.0 };
  if (isMuted) {
    return <VolumeOff />;
  }
  if (volume <= 0) {
    return <VolumeOff />;
  }
  if (volume <= 0.5 && volume > 0) {
    return <Volume1 />;
  }
  return <Volume2 />;
};

export interface MediaControlsProps {
  debugMode?: boolean;
  defaultStayLive?: boolean;
  live?: boolean;
  onChangeStayLive?: (stayLive: boolean) => void;
}

export default function MediaControls({
  debugMode = false,
  defaultStayLive = true,
  live = false,
  onChangeStayLive,
}: MediaControlsProps) {
  const { mediaApi, mediaState } = useContext(MediaElementContext);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const [stayLive, setStayLive] = useState(defaultStayLive);

  const handleClickPlayPause = () => {
    if (mediaState?.isPaused) {
      mediaApi?.play?.();
    } else {
      mediaApi?.pause?.();
    }
  };

  const handleChangeVolume = (newValue: number | number[]) => {
    mediaApi?.setVolume?.(newValue as number);
  };

  const handleClickMute = () => {
    if (mediaState?.isMuted) {
      mediaApi?.unMute?.();
    } else {
      mediaApi?.mute?.();
    }
  };

  const handleChangeMediaProgress = (newValue: number | number[]) => {
    const seekToValue = (mediaState.duration * (newValue as number)) / 100;
    mediaApi?.seekTo?.(seekToValue);
  };

  const handleClickSubtitle = () => {
    if (mediaState.subtitleEnabled) {
      mediaApi?.disableSubtitles?.();
    } else {
      mediaApi?.enableSubtitles?.('en');
    }
  };

  const handleChangeStayLive = () => {
    const newStayLive = !stayLive;
    setStayLive(newStayLive);
    onChangeStayLive?.(newStayLive);
    mediaApi?.seekTo?.(mediaState.duration);
    mediaApi?.play?.();
  };

  useEffect(() => {
    const { canPlay, currentTime, duration } = mediaState;
    const progressUpdate = (currentTime / duration) * 100;
    setDuration(duration);
    setCurrentTime(currentTime);
    setProgress(Number.isNaN(progressUpdate) ? 0 : progressUpdate);
    setDisabled(!canPlay);
  }, [mediaState]);

  const shouldRespectStayLive = stayLive && live;

  return (
    <div className="flex flex-col rounded-sm border-2">
      {debugMode ? (
        <div className="flex justify-center pt-1">
          <p className="rounded-sm border border-dashed border-primary p-1 text-[11px]">{`${formatSecondsHHMMSS(
            currentTime,
          )} / ${formatSecondsHHMMSS(duration)} (behind: ${Math.round(
            duration - currentTime,
          )})`}</p>
        </div>
      ) : null}
      <div className="flex items-center justify-center gap-2">
        <Button
          aria-label="Play/pause button"
          className="size-6 rounded-full p-1"
          disabled={disabled || shouldRespectStayLive}
          variant="ghost"
          onClick={handleClickPlayPause}>
          {mediaState?.isPaused ? <Play /> : <Pause />}
        </Button>
        <MediaSlider
          disabled={disabled}
          label="Volume slider"
          max={1.0}
          min={0.0}
          step={0.01}
          toolTipLabel={`${Math.floor(mediaState?.volume * 100)}%`}
          values={[mediaState?.volume ?? 1.0]}
          onChange={handleChangeVolume}
        />
        <Button
          aria-label="Mute button"
          className="size-6 rounded-full p-1"
          disabled={disabled}
          variant="ghost"
          onClick={handleClickMute}>
          {getVolumeIcon(mediaState)}
        </Button>
        <MediaSlider
          disabled={disabled || shouldRespectStayLive}
          label="Media progress"
          max={100}
          min={0}
          step={0.01}
          toolTipLabel={formatSecondsHHMMSS(currentTime)}
          values={[progress]}
          onChange={handleChangeMediaProgress}
        />
        <Button
          aria-label="Subtitle enable/disable button"
          className="size-6 rounded-full p-1"
          disabled={disabled}
          variant="ghost"
          onClick={handleClickSubtitle}>
          {mediaState.subtitleEnabled ? <Captions /> : <CaptionsOff />}
        </Button>
        {live && (
          <div className="flex items-center gap-1">
            <Switch
              checked={stayLive}
              id="keep-live"
              size="sm"
              onCheckedChange={handleChangeStayLive}
            />
            <Label className="cursor-pointer text-[11px]" htmlFor="keep-live">
              Stay Live
            </Label>
          </div>
        )}
        {mediaState.audioTracks?.length > 0 ? (
          <div className="flex flex-col items-center gap-1 p-1">
            <p className="text-xs font-medium">Current Audio Track:</p>
            <AudioSelector
              audioTracks={mediaState.audioTracks}
              selectedTrackIndex={mediaState.audioTrackIndex}
              onTrackSelected={(trackIndex) => mediaApi?.switchAudioTrack?.(trackIndex)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
